import { useCallback, useRef } from 'react';

const useHoverIntent = <T extends HTMLElement = HTMLElement>(
    minimumHoverMillis: number,
    callbackFunction: () => void,
) => {
    const timerRef = useRef<number | null>(null);
    const previousNode = useRef<T | null>(null);

    const handleMouseEnter = useCallback(() => {
        timerRef.current = window.setTimeout(() => {
            callbackFunction();
        }, minimumHoverMillis);
    }, [minimumHoverMillis, callbackFunction]);

    const handleMouseLeave = useCallback(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    }, []);

    const hoverRef = useCallback(
        (node: T | null) => {
            if (previousNode.current?.nodeType === Node.ELEMENT_NODE) {
                previousNode.current.removeEventListener('mouseenter', handleMouseEnter);
                previousNode.current.removeEventListener('mouseleave', handleMouseLeave);
            }
            if (node?.nodeType === Node.ELEMENT_NODE) {
                node.addEventListener('mouseenter', handleMouseEnter);
                node.addEventListener('mouseleave', handleMouseLeave);
            }
            previousNode.current = node;
        },
        [handleMouseEnter, handleMouseLeave],
    );

    return [hoverRef];
};

export default useHoverIntent;
